/**
 * Why a fund is on the grey or red list.
 *
 * A code and, where the sentence names another fund, that fund's ticker:
 * `s`, `h:HDIV`, `u:HYLD`.
 *
 * The wording lives here rather than in the data because it is display copy —
 * the same four sentences repeated across 65 records, 8.8K of prose to say one
 * of four things. Keeping it as a controlled vocabulary also stops the red list
 * accumulating near-identical phrasings, which matters more than the bytes: a
 * rejection reason is a public statement about somebody's product and it should
 * read the same way every time.
 */
export type ReasonCode = 's' | 'b' | 'h' | 'u';

export interface ReasonMeta {
    /** `{of}` is replaced by the ticker the code carries. */
    template: string;
    /** Whether the sentence needs one. */
    parameterised: boolean;
}

export const REASONS: Record<string, ReasonMeta> = {
    s: {
        template:
            'Single underlying company. Not a diversified income fund, and the distribution depends entirely on option premiums written against one name.',
        parameterised: false,
    },
    b: {
        template:
            'Diversified basket rather than a single company, unlike the rest of the High Income Shares range. Not yet audited.',
        parameterised: false,
    },
    h: { template: 'Held by {of}. Not yet audited on its own.', parameterised: true },
    u: { template: 'USD class of {of} — same portfolio, listed separately only for currency of settlement.', parameterised: true },
};

/** Build a code, with its ticker where the sentence takes one. */
export const reasonCode = (code: ReasonCode, of?: string): string => (of ? `${code}:${of}` : code);

/**
 * The sentence for a code. Returns the code itself if it is one we do not know,
 * rather than nothing — a red-list entry showing a bare `x` is wrong but
 * visible, where a blank reads as no reason given, which is the one thing the
 * red list must never do.
 */
export const reasonText = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const [code, of] = raw.split(':');
    const meta = REASONS[code];
    if (!meta) return raw;
    return of ? meta.template.replace('{of}', of) : meta.template;
};
